import { useLazyQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FETCH_PERCH_PERSONALIZED_CONTRACT } from '~/components/personalized-contract-link/personalized-contract-link.queries';
import { createPersonalizedContractPreview } from '~/services/create-personalized-contract-preview';
import {
  Flex_FetchPerchPersonalizedContractQuery,
  Flex_FetchPerchPersonalizedContractQueryVariables,
} from '~/typings/graphql';
import { IS_PERCH_ENERGY } from '~/utils/constants';

// Custom hook to fetch personalized contract
export const usePersonalizedContract = ({
  contractId,
  utilityAccountId,
  planId,
  tabsData,
}: {
  contractId: number;
  utilityAccountId?: number;
  planId?: number;
  tabsData?: {
    houseOccupancy?: string;
    lmiProgram?: string[];
    county?: string;
    houseIncome?: string;
    lmiSourceSelection?: string[];
  };
}) => {
  const isPerchEnergy = !!planId && IS_PERCH_ENERGY;
  const [personalizedFile, setPersonalizedFile] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [fetchPerchContract, perchQuery] = useLazyQuery<
    Flex_FetchPerchPersonalizedContractQuery,
    Flex_FetchPerchPersonalizedContractQueryVariables
  >(FETCH_PERCH_PERSONALIZED_CONTRACT);

  // For Perch Energy, use the query result
  useEffect(() => {
    if (perchQuery.data?.contractVersion?.perchPersonalizedFile) {
      setPersonalizedFile(perchQuery.data.contractVersion.perchPersonalizedFile);
      setIsLoading(false);
    }
  }, [perchQuery.data]);

  useEffect(() => {
    if (perchQuery.error) {
      setError(perchQuery.error);
      setIsLoading(false);
    }
  }, [perchQuery.error]);

  useEffect(() => {
    const fetchContract = async () => {
      setIsLoading(true);
      setError(null);

      if (isPerchEnergy) {
        // Fetch Perch personalized contract
        await fetchPerchContract({
          variables: { contractVersionId: contractId, planId: planId as number },
        });
      } else if (utilityAccountId) {
        // Fetch regular personalized contract using the mutation
        try {
          const response = await createPersonalizedContractPreview({
            contractVersionId: contractId,
            utilityAccountId,
            tabsData: tabsData || {},
          });

          const pdfUrl = response.base64.startsWith('data:')
            ? response.base64
            : `data:application/pdf;base64,${response.base64}`;
          setPersonalizedFile(pdfUrl);
          setIsLoading(false);
        } catch (err) {
          setError(
            err instanceof Error
              ? err
              : new Error('Failed to create personalized contract preview'),
          );
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchContract();
  }, [contractId, utilityAccountId, planId, isPerchEnergy, tabsData, fetchPerchContract]);

  return {
    personalizedFile,
    error,
    isLoading,
    isPerchEnergy,
  };
};
